import { useRef, useState } from 'react'
import { FitpetIcon } from '@fitpet/ui/components'
import { clsx } from 'clsx'
import router from 'next/router'
import Slider from 'react-slick'
import {
  HomeSectionCategoryResponseDto,
  ProductsCategoryResponseDtoAllOfChildrenItem,
  ProductsCategoryScheme,
} from '@/apis/rest/generated/types'
import { CarouselBaseV2 } from '@/components/common/carousels/CarouselBaseV2'
import { Image } from '@/components/common/images/Image'
import { CategoryItemList } from '@/components/domains/home/new/home-section-list/category-section/category-item-list'
import styles from '@/components/domains/home/new/home-section-list/category-section/home-section-category.module.css'
import {
  CategoryItem,
  RecommendCategoryRootItem,
} from '@/components/domains/home/new/home-section-list/category-section/types/category-section.type'
import { ENV_CONFIG } from '@/constants/env/config'
import { FitpetMallEventEnum } from '@/constants/fitpet-mall-event.enum'
import ROUTES from '@/constants/legacy/constRoutes'
import { useTracker } from '@/containers/contexts/EventTrackingProvider'
import { AdPetType } from '@/graphql/generated/schemas'
import { localPetType } from '@/utils/utilLocalStorage'
import { getImageByStaticUrl } from '@/utils/utilURL'

const CATEGORY_FIRST_VIEW_COUNT = 10
const RECOMMEND_CATEGORY_ID = 1
const NYANG_SSAM_CATEGORY_ID = -1
const CAROUSEL_SPEED = 200
const CAROUSEL_SLIDES_TO_SHOW = 1

const createAllCategory = (parentCategory: ProductsCategoryResponseDtoAllOfChildrenItem): ProductsCategoryScheme => ({
  id: 1,
  code: `${parentCategory.code}000`,
  depth: 2,
  description: '',
  displayOrder: (parentCategory.children?.length ?? 0) + 1,
  isVisible: true,
  listImage: `${ENV_CONFIG.fitpetmallHost}/icons/ic_add.svg`,
  name: '전체',
  parentCategoryId: 0,
  slug: 'all',
  text: parentCategory.text,
})

const transformNormalCategories = (categories: HomeSectionCategoryResponseDto['categories'] = []): CategoryItem[] => {
  const rootChildren = categories[0]?.children || []

  return rootChildren.map((category) => ({
    ...category,
    children: category.children ? [...category.children, createAllCategory(category)] : [],
  }))
}

const createRecommendCategory = (
  categoryQuickMenus: HomeSectionCategoryResponseDto['categoryQuickMenus'] = []
): RecommendCategoryRootItem => {
  return {
    id: RECOMMEND_CATEGORY_ID,
    name: '추천' as const,
    children: categoryQuickMenus.map((category) => ({
      ...category,
      id: category.displayOrder,
      slug: '',
    })),
  }
}

interface HomeSectionCategoryProps {
  homeSectionItem: HomeSectionCategoryResponseDto
}

export const HomeSectionCategory = ({ homeSectionItem }: HomeSectionCategoryProps) => {
  const tracker = useTracker()
  const sliderRef = useRef<Slider>(null)
  const [isOpen, setIsOpen] = useState(false)

  const recommendCategory = createRecommendCategory(homeSectionItem.categoryQuickMenus)
  const normalCategoryList = transformNormalCategories(homeSectionItem.categories)

  const isCat = localPetType.load() === AdPetType.Cat

  const getInitialCategoryId = () => {
    if (isCat) {
      return Math.random() < 0.5 ? RECOMMEND_CATEGORY_ID : NYANG_SSAM_CATEGORY_ID
    }
    return RECOMMEND_CATEGORY_ID
  }

  const [selectedRootCategoryId, setSelectedRootCategoryId] = useState(getInitialCategoryId)

  const nyangSsamCategory = {
    id: NYANG_SSAM_CATEGORY_ID,
    name: '냥쌤',
    children: [],
  } as unknown as CategoryItem

  const rootCategory: CategoryItem[] = isCat
    ? [recommendCategory, nyangSsamCategory, ...normalCategoryList]
    : [recommendCategory, ...normalCategoryList]

  const initialSlideIndex = rootCategory.findIndex((cat) => cat.id === selectedRootCategoryId)

  const rootCategoryIndex = rootCategory.findIndex((category) => category.id === selectedRootCategoryId)
  const selectedRootCategory = rootCategory[rootCategoryIndex]

  const carouselData = rootCategory.map((category) => {
    return {
      id: category.id,
      name: category.name,
      subCategories: category.children?.filter((edge) => edge?.slug !== 'sample') || [],
    }
  })

  const handleSwipeChange = (actionType: 'left' | 'right') => {
    const newIndex =
      actionType === 'right'
        ? (rootCategoryIndex - 1 + rootCategory.length) % rootCategory.length
        : (rootCategoryIndex + 1) % rootCategory.length
    setSelectedRootCategoryId(rootCategory[newIndex].id)
  }

  const handleCategorySelected = (selectedId: number) => {
    setIsOpen(false)
    const rootCategoryIndex = rootCategory.findIndex((category) => category.id === selectedId)
    sliderRef.current?.slickGoTo(rootCategoryIndex)

    const categoryTypeName = rootCategory.find((item) => item.id === selectedId)?.name
    tracker.triggerCustomEvent(FitpetMallEventEnum.ClickHomeCategoryTab, { categoryTypeName })
    setSelectedRootCategoryId(selectedId)
  }

  const handleOpenSeeMore = () => {
    setIsOpen(true)
    tracker.triggerCustomEvent(FitpetMallEventEnum.ClickHomeCategoryItem, {
      categoryName: '전체보기',
    })
  }

  if (!homeSectionItem?.categories?.[0]?.children?.length) {
    return null
  }

  return (
    <>
      <div className={styles.categoryWrapper}>
        <div className={styles.tabContainer}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <CategoryTab
              id={RECOMMEND_CATEGORY_ID}
              name="추천"
              isSelected={selectedRootCategoryId === RECOMMEND_CATEGORY_ID}
              onClick={handleCategorySelected}
            />
            {isCat && (
              <CategoryTab
                id={NYANG_SSAM_CATEGORY_ID}
                name="냥쌤"
                isSelected={selectedRootCategoryId === NYANG_SSAM_CATEGORY_ID}
                onClick={handleCategorySelected}
              />
            )}
          </div>

          <div className={styles.normalTabWrapper}>
            {rootCategory.slice(isCat ? 2 : 1).map(({ id, name }) => (
              <CategoryTab
                key={id}
                id={id}
                name={name}
                isSelected={selectedRootCategoryId === id}
                onClick={handleCategorySelected}
              />
            ))}
          </div>
        </div>

        <CarouselBaseV2
          ref={sliderRef}
          onSwipe={handleSwipeChange}
          slidesToShow={CAROUSEL_SLIDES_TO_SHOW}
          initialSlide={initialSlideIndex}
          lazyLoad
          NextArrow={<></>}
          PreviousArrow={<></>}
          speed={CAROUSEL_SPEED}
        >
          {carouselData.map((rootCategory) => {
            if (rootCategory.id === NYANG_SSAM_CATEGORY_ID) {
              return (
                <div
                  className={styles.image}
                  key={rootCategory.id}
                  onClick={() => router.push(ROUTES.EVENT_DETAIL.replace('[adSlug]', 'B_nyangssaem_main_cat'))}
                >
                  <Image
                    src={getImageByStaticUrl('category/category-nyangssam.png')}
                    alt="냥쌤 배너"
                    aspectRatio="687 / 304"
                  />
                </div>
              )
            }

            const isShowSeeMore = rootCategory.subCategories.length > CATEGORY_FIRST_VIEW_COUNT
            const displayCategories =
              isOpen || rootCategory.id === RECOMMEND_CATEGORY_ID
                ? rootCategory.subCategories
                : rootCategory.subCategories.slice(0, CATEGORY_FIRST_VIEW_COUNT - 1)

            return (
              <div className={styles.list} key={rootCategory.id}>
                {displayCategories.map((subCategory) => (
                  <CategoryItemList
                    key={subCategory.id}
                    subCategory={subCategory}
                    selectedRootCategory={selectedRootCategory}
                  />
                ))}
                {!isOpen && isShowSeeMore && <SeeMoreButton onClick={handleOpenSeeMore} />}
              </div>
            )
          })}
        </CarouselBaseV2>
      </div>
      <div className={styles.divider} />
    </>
  )
}

const CategoryTab = ({
  id,
  name,
  isSelected,
  onClick,
}: {
  id: number
  name: string
  isSelected: boolean
  onClick: (id: number) => void
}) => {
  const isIndependentTab = id === RECOMMEND_CATEGORY_ID || id === NYANG_SSAM_CATEGORY_ID

  return (
    <button
      type="button"
      className={isIndependentTab ? styles.recommendTabButton : styles.normalTabButton}
      data-selected={isSelected}
      onClick={() => onClick(id)}
    >
      <span
        className={clsx({
          'font-body3-bold gray-white': isSelected,
          'font-body3-medium gray-500': !isSelected,
        })}
      >
        {name}
      </span>
    </button>
  )
}

const SeeMoreButton = ({ onClick }: { onClick: () => void }) => (
  // ... 생략 (기존과 동일)
  <div className={styles.seeMoreCol} onClick={onClick}>
    <div className={styles.seeMoreItem}>
      <div className={styles.imageRow}>
        <FitpetIcon icon="icChevronDown" size={20} />
      </div>
    </div>
    <div className={styles.textRow}>
      <span className="font-body5-regular">더 보기</span>
    </div>
  </div>
)
